/**
 * useAtendimentos - Hook personalizado para gerenciamento de atendimentos
 * 
 * Este hook encapsula toda a lógica de carregamento, filtragem e manipulação de dados
 * de atendimentos, incluindo:
 *   - Busca de dados da API
 *   - Gerenciamento de estados de filtros
 *   - Lógica de filtragem
 *   - Controle de estado de carregamento
 */

import { useEffect, useState, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode'; // Para decodificar o token JWT e obter o ID do cliente
import axios from 'axios';              // Para requisições HTTP à API
import { convertToInputDate } from '../utils/date'; // Utilitário para manipulação de datas

/**
 * Hook useAtendimentos
 * 
 * @param {number} tabIndex - Índice da aba atualmente selecionada
 * @param {string} baseURL - URL base para as requisições à API
 * @returns {Object} Objeto contendo estados e funções para manipulação de atendimentos
 */
export default function useAtendimentos(tabIndex, baseURL, setForceTokenExpired) {

  // Estados para armazenar os dados de atendimentos e controlar o carregamento
  const [atendimentos, setAtendimentos] = useState([]); // Todos os atendimentos obtidos da API
  const [loading, setLoading] = useState(false);        // Estado de carregamento (requisição em andamento)

  // Estados para os filtros
  const [dateFilter, setDateFilter] = useState('Mês atual');           // Filtro de período (padrão: 'Mês atual')
  const [situacaoFilter, setSituacaoFilter] = useState('Todos');       // Filtro de situação (padrão: 'Todos')
  const [whatsAppIdFilter, setWhatsAppIdFilter] = useState('');        // Filtro de WhatsAppId (padrão: vazio)
  const [dataFilter, setDataFilter] = useState('');                    // Filtro de data específica (padrão: vazio)

  /**
   * Limpa todos os filtros, restaurando seus valores padrão
   */
  const handleClearFilters = () => {
    setDateFilter('Mês atual');
    setSituacaoFilter('Todos');
    setWhatsAppIdFilter('');
    setDataFilter('');
  };

  /**
   * Efeito para buscar atendimentos da API quando a aba muda ou o filtro de data é alterado
   * Só executa a busca quando a aba é 0 ou 1 (presumivelmente abas que exibem atendimentos)
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Verifica se a aba atual deve exibir atendimentos
    if (tabIndex === 0 || tabIndex === 1) {
      /**
       * Função assíncrona para buscar atendimentos da API
       */
      const fetchAtendimentos = async () => {
        setLoading(true); // Inicia o estado de carregamento
        try {
          // Obtém o token JWT do armazenamento local
          const token = localStorage.getItem('jwtToken');
          if (token) {
            // Decodifica o token para obter o ID do cliente
            const decodedToken = jwtDecode(token);
            const idCliente = decodedToken.id_cliente;

            // Prepara os parâmetros da requisição, começando com o ID do cliente
            let params = { idCliente };

            // Adiciona o parâmetro de dias se o filtro não for 'Todos'
            if (dateFilter !== 'Todos') {
              let dias;
              // Calcula o número de dias para o filtro selecionado
              if (dateFilter === 'Mês atual') {
                // Para 'Mês atual', usa o dia do mês atual (ex: se hoje é dia 15, busca os últimos 15 dias)
                dias = new Date().getDate();
              } else {
                // Mapeamento de filtros textuais para número de dias
                const map = {
                  'Últimos 7 dias': 7,
                  'Últimos 30 dias': 30,
                  'Últimos 3 meses': 90,
                  'Últimos 6 meses': 180,
                  'Últimos 12 meses': 365
                };
                dias = map[dateFilter] || 0;
              }
              params.dias = dias; // Adiciona dias aos parâmetros
            }

            // Faz a requisição para a API
            const response = await axios.get(`${baseURL}/produto/posv/atendimentos-cliente`, {
              params,
              headers: { Authorization: `Bearer ${token}` } // Inclui o token de autenticação
            });

            // Atualiza o estado com os dados recebidos
            setAtendimentos(response.data);
          }
        } catch (error) {
          // Registra erros no console para depuração
          console.error('Erro ao buscar atendimentos:', error);

          if (
            error.response?.status === 401 ||
            error.response?.data?.message?.toLowerCase().includes("token") // ou "expired"
          ) {
            setForceTokenExpired(true); // Força abrir o diálogo
          }
        }
        setLoading(false); // Finaliza o estado de carregamento
      };

      // Executa a busca de atendimentos
      fetchAtendimentos();
    }
  }, [tabIndex, dateFilter, baseURL, setForceTokenExpired]); // Dependências: executa novamente quando a aba ou filtro de data mudam

  /**
   * Calcula os atendimentos filtrados com base nos filtros atuais
   * Usa useMemo para evitar recálculos desnecessários (melhor performance)
   */
  const filteredAtendimentos = useMemo(() => {
    return atendimentos.filter(atendimento => {
      // Filtro por situação (concluído/em aberto)
      if (situacaoFilter !== 'Todos') {
        if (situacaoFilter === 'Concluído' && !atendimento.concluido) return false;
        if (situacaoFilter === 'Em Aberto' && atendimento.concluido) return false;
      }

      // Filtro por WhatsAppId (texto parcial, case insensitive)
      if (whatsAppIdFilter.trim() !== '') {
        if (!atendimento.whatsAppId.toLowerCase().includes(whatsAppIdFilter.toLowerCase())) return false;
      }

      // Filtro por data específica (correspondência exata)
      if (dataFilter.trim() !== '') {
        if (convertToInputDate(atendimento.dataHoraCadastroAtualizado) !== dataFilter) return false;
      }

      // Se passar por todos os filtros, o atendimento é incluído
      return true;
    });
  }, [atendimentos, situacaoFilter, whatsAppIdFilter, dataFilter]); // Dependências do useMemo

  // Retorna todos os estados e funções necessárias para o componente
  return {
    atendimentos,              // Lista completa de atendimentos (não filtrada)
    filteredAtendimentos,      // Lista de atendimentos após aplicação dos filtros
    loading,                   // Estado de carregamento
    dateFilter,                // Estado atual do filtro de período
    setDateFilter,             // Função para atualizar o filtro de período
    situacaoFilter,            // Estado atual do filtro de situação
    setSituacaoFilter,         // Função para atualizar o filtro de situação
    whatsAppIdFilter,          // Estado atual do filtro de WhatsAppId
    setWhatsAppIdFilter,       // Função para atualizar o filtro de WhatsAppId
    dataFilter,                // Estado atual do filtro de data específica
    setDataFilter,             // Função para atualizar o filtro de data
    handleClearFilters         // Função para limpar todos os filtros
  };
}